/**
 * EditPromoterModal.tsx - Promoter Information Editing Modal
 *
 * This component provides an interface for editing promoter information
 * including contact details, goals, and performance settings.
 *
 * Key Features:
 * - Promoter contact information editing
 * - Goal setting and adjustment
 * - Performance tracking configuration
 * - Form validation with error handling
 * - Real-time updates to promoter data
 * - Mobile-responsive form design
 *
 * Editable Fields:
 * - Name and contact information
 * - Email and phone number
 * - Fundraising goal amount
 * - Status and activity settings
 *
 * Props:
 * - isVisible: boolean - Modal visibility state
 * - onClose: () => void - Close modal handler
 * - promoter: Promoter | null - Promoter to edit
 * - onSave: (promoter: Promoter) => void - Save changes handler
 *
 * Dependencies: Promoter types, MobX store, form validation, portal rendering
 */

import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { fundraiserStore } from "../stores/FundraiserStore";
import { Toast } from "./Toast";
import {
  ModalOverlay,
  ModalContent,
  FormGroup,
  FormLabel,
  FormInput,
  PrimaryButton,
  SecondaryButton,
} from "./shared/StyledComponents";
import styled from "styled-components";
import { PromoterProgressModel, UpdatePromoterContactModel } from "../models";
import {
  ContactService,
  CountryPhoneDropdown,
  CountryWithStateModel,
} from "@mindmemobile/mm-payment-modules";
import { IAPIEditPromoterRequest } from "../interfaces";
import { FundraiserService } from "../services";
import ToastService from "./ToastService";

const StyledModalContent = styled(ModalContent)`
  max-width: 600px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.1);
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: rgba(0, 0, 0, 0.2);
    color: #1e293b;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ModalHeader = styled.div`
  padding: 32px 40px 24px;
  text-align: center;
`;

const ModalTitle = styled.h2`
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
`;

const ContentContainer = styled.div`
  padding: 25px 40px 0px;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  width: 100%;
  max-width: 480px;
`;

const GoalInputContainer = styled.div`
  max-width: 440px;
  position: relative;
`;

const GoalCurrencySymbol = styled.span`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
  z-index: 1;
`;

const GoalInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px 16px 12px 40px;
  padding-right: ${(props) =>
    props.hasError
      ? "140px"
      : props.value && parseFloat(props.value as string) > 0
        ? "50px"
        : "16px"};
  border: 2px solid ${(props) => (props.hasError ? "#dc2626" : "#e2e8f0")};
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "#dc2626" : "#10b981")};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.hasError ? "rgba(220, 38, 38, 0.1)" : "rgba(16, 185, 129, 0.1)"};
  }

  /* Hide number input spinners */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  &[type="number"] {
    -moz-appearance: textfield;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ClearGoalButton = styled.button<{ visible: boolean }>`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border: none;
  background: #f1f5f9;
  border-radius: 50%;
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #64748b;

  &:hover {
    background: #e2e8f0;
    color: #374151;
    transform: translateY(-50%) scale(1.1);
  }

  &:active {
    transform: translateY(-50%) scale(0.9);
  }
`;

const ValidationText = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  font-weight: 600;
  color: #dc2626;
  pointer-events: none;
  white-space: nowrap;
`;

const StatusSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 0px;
`;

const StatusButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
`;

const StatusBadge = styled.button<{ selected: boolean; status: string }>`
  padding: 12px 16px;
  border: 2px solid
    ${(props) => {
      if (props.selected) {
        return props.status === "active" ? "#166534" : "#92400e";
      }
      return "#9ca3af";
    }};
  border-radius: 8px;
  background: ${(props) => {
    if (props.selected) {
      return props.status === "active" ? "#dcfce7" : "#fef3c7";
    }
    return "#f3f4f6";
  }};
  color: ${(props) => {
    if (props.selected) {
      return props.status === "active" ? "#166534" : "#92400e";
    }
    return "#6b7280";
  }};
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) =>
      props.status === "active" ? "#166534" : "#92400e"};
    background: ${(props) =>
      props.status === "active" ? "#dcfce7" : "#fef3c7"};
    color: ${(props) => (props.status === "active" ? "#166534" : "#92400e")};
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 15px;
  margin-bottom: 20px;
`;

interface EditPromoterModalProps {
  isVisible: boolean;
  onClose: () => void;
  promoter: PromoterProgressModel | null;
  onUpdate: (updatedPromoter: PromoterProgressModel) => void;
}

export const EditPromoterModal: React.FC<EditPromoterModalProps> = ({
  isVisible,
  onClose,
  promoter,
  onUpdate,
}) => {
  const countries = fundraiserStore.countryStore.countriesWithState;
  const usCountry = countries.find((c) => c.getUSCountry());
  const defaultFormData = new UpdatePromoterContactModel({
    countryId: usCountry?.id || "",
    countryCode: usCountry?.code?.toString() || "",
    isActive: true,
  });

  const [formData, setFormData] =
    useState<UpdatePromoterContactModel>(defaultFormData);
  const [goalValidationError, setGoalValidationError] = useState("");

  const [hasChanges, setHasChanges] = useState(false);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  useEffect(() => {
    if (isVisible) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isVisible]);

  // Pre-populate form when promoter data is provided
  useEffect(() => {
    if (isVisible && promoter) {
      // Check if fundraiser has no goal (0 or null)
      const fundraiserGoal =
        fundraiserStore.builderData?.goal ||
        fundraiserStore.selectedFundraiser?.goal ||
        0;
      const shouldSetGoalToZero =
        fundraiserGoal === 0 || fundraiserGoal === null;

      const newFormData = new UpdatePromoterContactModel({
        firstName: promoter.firstName,
        lastName: promoter.lastName,
        email: promoter.email,
        mobile: promoter.mobile,
        goalAmount: shouldSetGoalToZero
          ? "0"
          : promoter.goalAmount > 0
            ? promoter.goalAmount.toString()
            : "0",
        isActive: promoter.isActive === true,
        countryId: promoter.countryId || usCountry?.id || "",
        countryCode: promoter.countryCode || usCountry?.code?.toString() || "",
      });

      setFormData(newFormData);
      setGoalValidationError("");
    }
  }, [isVisible, promoter]);

  // Track changes in form data
  useEffect(() => {
    const formHasChanges =
      formData.firstName !== defaultFormData.firstName ||
      formData.lastName !== defaultFormData.lastName ||
      formData.email !== defaultFormData.email ||
      formData.mobile !== defaultFormData.mobile ||
      formData.goalAmount !== defaultFormData.goalAmount ||
      formData.isActive !== defaultFormData.isActive;
    setHasChanges(formHasChanges);
  }, [formData, defaultFormData]);

  const handleGoalChange = (value: string) => {
    // Allow only numbers and decimal points
    const numericValue = value.replace(/[^0-9.]/g, "");

    updateFormInfo("goalAmount", numericValue);

    // Validate against fundraiser goal
    const fundraiserGoal =
      fundraiserStore.builderData?.goal ||
      fundraiserStore.selectedFundraiser?.goal ||
      0;

    if (fundraiserGoal > 0 && parseFloat(numericValue) > fundraiserGoal) {
      setGoalValidationError(`Max ${formatCurrency(fundraiserGoal)}`);
    } else {
      setGoalValidationError("");
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const updateFormInfo = (
    field: keyof UpdatePromoterContactModel,
    value: string | number | boolean
  ) => {
    const updatedFormData = new UpdatePromoterContactModel({
      ...formData,
      [field]: value,
    });
    setFormData(updatedFormData);
  };

  const handleUpdate = () => {
    if (!promoter || !promoter.id) return;

    if (!formData.validate()) {
      setFormData(
        (prevData) => new UpdatePromoterContactModel({ ...prevData })
      );
      return;
    }

    // Convert goal to integer, handling decimals by truncating
    const goalValue = parseFloat(formData.goalAmount) || 0;
    const goalAsInteger = Math.floor(Math.max(0, goalValue));

    if (!promoter.id || !promoter.fundraiserId) {
      addNewPromoter(goalAsInteger, promoter.id);
      onClose();
      ToastService.showSuccess("Promoter added successfully");
    }

    const request = UpdatePromoterContactModel.createAPIRequestModel(
      promoter.fundraiserId,
      promoter.contactId,
      goalAsInteger,
      formData
    );
    FundraiserService.updatePromoter(request).subscribe({
      next: (response) => {
        if (response.result) {
          addNewPromoter(goalAsInteger, promoter.id);
          onClose();
          ToastService.showSuccess("Promoter updated successfully");
        }
      },
      error: () => {
        ToastService.showError("Error in updated promoter");
      },
    });
  };

  const addNewPromoter = (goalAmount: number, contactId: string) => {
    const updatedPromoter = new PromoterProgressModel({
      ...promoter,
      firstName: formData.firstName,
      lastName: formData.lastName,
      displayName: `${formData.firstName} ${formData.lastName}`.trim(),
      email: formData.email,
      mobile: formData.mobile,
      goalAmount: goalAmount,
      isActive: formData.isActive,
    });
    onUpdate(updatedPromoter);
  };

  const isFormValid = () => {
    return formData.email.trim() !== "" && !goalValidationError && hasChanges;
  };

  const clearGoal = () => {
    updateFormInfo("goalAmount", 0);
    setGoalValidationError("");
  };

  if (!promoter) return null;

  return createPortal(
    <ModalOverlay visible={isVisible} onClick={handleOverlayClick}>
      <StyledModalContent>
        <CloseButton onClick={onClose}>
          <X size={20} />
        </CloseButton>

        <ModalHeader>
          <ModalTitle>Edit Promoter</ModalTitle>
        </ModalHeader>

        <ContentContainer onClick={(e) => e.stopPropagation()}>
          <FormSection>
            <FormRow>
              <FormGroup>
                <FormLabel htmlFor="firstName">First Name</FormLabel>
                <FormInput
                  id="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => updateFormInfo("firstName", e.target.value)}
                  placeholder="Enter first name"
                  hasError={formData.hasError("firstName")}
                  maxLength={80}
                />
              </FormGroup>
              <FormGroup>
                <FormLabel htmlFor="lastName">Last Name</FormLabel>
                <FormInput
                  id="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => updateFormInfo("lastName", e.target.value)}
                  placeholder="Enter last name"
                  hasError={formData.hasError("lastName")}
                  maxLength={80}
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <FormLabel htmlFor="email">Email Address</FormLabel>
              <FormInput
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => updateFormInfo("email", e.target.value)}
                placeholder="Enter email address"
                maxLength={100}
              />
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="mobile">Mobile Phone</FormLabel>
              <CountryPhoneDropdown
                selectedCountry={
                  countries.find((c) => c.id === formData.countryId) ||
                  new CountryWithStateModel()
                }
                error={
                  formData.hasError("mobile") || formData.hasError("countryId")
                }
                errorMessage={"Please select country and enter phone number"}
                onCountryChange={(id: string) => {
                  const selectedCountry = countries.find((c) => c.id === id);
                  updateFormInfo("countryId", selectedCountry?.id || "");
                  updateFormInfo(
                    "countryCode",
                    selectedCountry?.code?.toString() || ""
                  );
                }}
                phoneNumber={formData.mobile}
                onPhoneNumberChange={(number: string) =>
                  updateFormInfo("mobile", number)
                }
                availableCountries={countries}
                className="country-dropdown-expanded"
              />
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="goal">Goal</FormLabel>
              <GoalInputContainer>
                <GoalCurrencySymbol>$</GoalCurrencySymbol>
                <GoalInput
                  id="goal"
                  type="number"
                  value={formData.goalAmount}
                  onChange={(e) => handleGoalChange(e.target.value)}
                  placeholder="0"
                  hasError={!!goalValidationError}
                  maxLength={8}
                />
                {goalValidationError && (
                  <ValidationText>{goalValidationError}</ValidationText>
                )}
                <ClearGoalButton
                  visible={
                    !!formData.goalAmount &&
                    parseFloat(formData.goalAmount) > 0 &&
                    !goalValidationError
                  }
                  onClick={clearGoal}
                  type="button"
                  title="Clear goal amount"
                >
                  <X size={12} />
                </ClearGoalButton>
              </GoalInputContainer>
            </FormGroup>

            <StatusSection>
              <FormLabel>Status</FormLabel>
              <StatusButtons>
                <StatusBadge
                  selected={formData.isActive}
                  status="active"
                  onClick={() => updateFormInfo("isActive", true)}
                  type="button"
                >
                  Active
                </StatusBadge>
                <StatusBadge
                  selected={!formData.isActive}
                  status="inactive"
                  onClick={() => updateFormInfo("isActive", false)}
                  type="button"
                >
                  Inactive
                </StatusBadge>
              </StatusButtons>
            </StatusSection>

            <ModalActions>
              <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
              <PrimaryButton disabled={!isFormValid()} onClick={handleUpdate}>
                Update
              </PrimaryButton>
            </ModalActions>
          </FormSection>
        </ContentContainer>
      </StyledModalContent>
    </ModalOverlay>,
    document.body
  );
};
